import { useEffect, useMemo, useRef, useState } from 'react'
import { Building2, Check, MoreHorizontal, Pencil, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  SearchDropdown,
  type SearchDropdownItem,
} from '@/components/ui/search-dropdown'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Heading, Text } from '@/components/ui/typography'
import { useAppAuth } from '@/contexts/app-auth.context'
import {
  directoryApiClient,
  type PropertyGroup,
  type PropertyGroupDeleteImpact,
  type PropertyGroupWithMembers,
} from '@/lib/api/directory.client'
import { directoryAdminApiClient } from '@/lib/api/directory-admin.client'
import type { Property } from '@super/shared-types'

// 'RENTVINE' → 'Rentvine', 'RENT_MANAGER' → 'Rent Manager'
const formatProvider = (integrationType: string | null | undefined): string =>
  (integrationType ?? 'PMS')
    .split('_')
    .map(word => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ')

const isSynced = (group: PropertyGroup) => group.source === 'INTEGRATION'

const propertyCityState = (property: Property): string | undefined => {
  const parts = [
    property.propertyAddress?.addressCity,
    property.propertyAddress?.addressState,
  ].filter(Boolean)
  return parts.length > 0 ? parts.join(', ') : undefined
}

export function PropertyGroupsTab(props: {
  properties: Property[]
  isLoadingProperties: boolean
  createSignal?: number
}) {
  const { properties, isLoadingProperties, createSignal } = props
  const { currentCompany, isSuperAdmin } = useAppAuth()
  const companyId = currentCompany?.id

  const [groups, setGroups] = useState<PropertyGroupWithMembers[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [editing, setEditing] = useState<
    PropertyGroupWithMembers | 'new' | null
  >(null)
  const [deleting, setDeleting] = useState<PropertyGroupWithMembers | null>(
    null,
  )

  const refresh = async () => {
    if (!companyId) return
    setIsLoading(true)
    try {
      const result = isSuperAdmin
        ? await directoryAdminApiClient.listPropertyGroups(companyId)
        : await directoryApiClient.listPropertyGroups()
      setGroups(result)
    } catch (error) {
      console.error('Failed to load property groups:', error)
      toast.error('Failed to load property groups')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    refresh()
  }, [companyId, isSuperAdmin])

  // The page-level "Add group" header button bumps this counter. Compare
  // against the value seen at mount — the counter survives tab switches, so
  // reacting to its mere presence would reopen the dialog on remount.
  const lastCreateSignal = useRef(createSignal ?? 0)
  useEffect(() => {
    if (
      createSignal !== undefined &&
      createSignal !== lastCreateSignal.current
    ) {
      lastCreateSignal.current = createSignal
      setEditing('new')
    }
  }, [createSignal])

  const propertyById = useMemo(
    () => new Map(properties.map(property => [property.id, property])),
    [properties],
  )

  if (isLoading || isLoadingProperties) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} className="h-24 w-full rounded-lg" />
        ))}
      </div>
    )
  }

  return (
    <div className="pb-20">
      {/* Creation lives on the page-header "Add group" button (via createSignal). */}
      <Heading variant="subsection-title" className="mb-4">
        Property groups ({groups.length})
      </Heading>

      <div className="space-y-3">
        {groups.map(group => {
          const synced = isSynced(group)
          const memberCount = group.propertyIds.length
          return (
            <div key={group.id} className="border rounded-lg p-4">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <div className="flex items-center gap-2">
                    <Heading variant="subsection-title">{group.name}</Heading>
                    <Badge variant="secondary">{memberCount} properties</Badge>
                    {synced && (
                      <TooltipProvider delayDuration={150}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge variant="outline">
                              Synced · {formatProvider(group.integrationType)}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            Managed by {formatProvider(group.integrationType)} —
                            membership updates on every property sync. Edit it
                            in the PMS instead.
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                  {group.description && (
                    <Text variant="muted">{group.description}</Text>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={synced}
                    onClick={() => setEditing(group)}
                  >
                    Edit group
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-40">
                      <DropdownMenuItem
                        disabled={synced}
                        onClick={() => setEditing(group)}
                      >
                        <Pencil className="w-4 h-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {/* Enabled even for synced groups — the server rejects
                          the delete while the integration is connected. */}
                      <DropdownMenuItem
                        onClick={() => setDeleting(group)}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {memberCount > 0 ? (
                <div className="flex flex-wrap gap-2 mt-2 items-center">
                  {group.propertyIds.slice(0, 12).map(propertyId => {
                    const property = propertyById.get(propertyId)
                    return (
                      <Badge
                        key={propertyId}
                        variant="outline"
                        className="cursor-default"
                      >
                        {property?.name || property?.externalId || propertyId}
                      </Badge>
                    )
                  })}
                  {memberCount > 12 && (
                    <Text variant="muted" className="text-xs">
                      +{memberCount - 12} more
                    </Text>
                  )}
                </div>
              ) : (
                <Text variant="muted" className="text-sm italic">
                  No properties in this group yet.
                </Text>
              )}
            </div>
          )
        })}
        {groups.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            No property groups yet. Click "Add group" to create one, or they
            will appear automatically if you sync your directory.
          </div>
        )}
      </div>

      {editing && (
        <PropertyGroupDialog
          group={editing === 'new' ? null : editing}
          properties={properties}
          companyId={companyId!}
          isSuperAdmin={isSuperAdmin}
          onSaved={() => {
            setEditing(null)
            refresh()
          }}
          onClose={() => setEditing(null)}
        />
      )}

      {deleting && (
        <DeleteGroupDialog
          group={deleting}
          companyId={companyId!}
          isSuperAdmin={isSuperAdmin}
          onDeleted={() => {
            setDeleting(null)
            refresh()
          }}
          onClose={() => setDeleting(null)}
        />
      )}
    </div>
  )
}

// One dialog for create and edit: name + description + member properties.
function PropertyGroupDialog(props: {
  group: PropertyGroupWithMembers | null
  properties: Property[]
  companyId: string
  isSuperAdmin: boolean
  onSaved: () => void
  onClose: () => void
}) {
  const { group, properties, companyId, isSuperAdmin, onSaved, onClose } = props

  const [name, setName] = useState(group?.name ?? '')
  const [description, setDescription] = useState(group?.description ?? '')
  const [selectedIds, setSelectedIds] = useState<string[]>(
    () => group?.propertyIds ?? [],
  )
  const [saving, setSaving] = useState(false)

  const items = useMemo<SearchDropdownItem<Property>[]>(
    () =>
      properties.map(property => ({
        id: property.id,
        label: property.name || property.externalId,
        sublabel: propertyCityState(property),
        icon: <Building2 className="h-4 w-4" />,
        data: property,
      })),
    [properties],
  )
  const selectedItems = useMemo(
    () => items.filter(item => selectedIds.includes(item.id)),
    [items, selectedIds],
  )

  const handleSelect = (
    selection:
      | SearchDropdownItem<Property>
      | SearchDropdownItem<Property>[]
      | null,
  ) => {
    const next = Array.isArray(selection)
      ? selection
      : selection
        ? [selection]
        : []
    setSelectedIds(next.map(item => item.id))
  }

  const save = async () => {
    if (!name.trim()) {
      toast.error('Group name is required')
      return
    }
    setSaving(true)
    try {
      // Membership is declarative: it becomes exactly the selection.
      const members = selectedIds

      if (group) {
        const input = {
          name: name.trim(),
          description: description.trim() || null,
        }
        if (isSuperAdmin) {
          await directoryAdminApiClient.updatePropertyGroup(
            companyId,
            group.id,
            input,
          )
          await directoryAdminApiClient.setPropertyGroupMembers(
            companyId,
            group.id,
            members,
          )
        } else {
          await directoryApiClient.updatePropertyGroup(group.id, input)
          await directoryApiClient.setPropertyGroupMembers(group.id, members)
        }
        toast.success(`Updated group "${input.name}"`)
      } else {
        const input = {
          name: name.trim(),
          description: description.trim() || undefined,
        }
        const created = isSuperAdmin
          ? await directoryAdminApiClient.createPropertyGroup(companyId, input)
          : await directoryApiClient.createPropertyGroup(input)
        if (members.length > 0) {
          if (isSuperAdmin) {
            await directoryAdminApiClient.setPropertyGroupMembers(
              companyId,
              created.id,
              members,
            )
          } else {
            await directoryApiClient.setPropertyGroupMembers(
              created.id,
              members,
            )
          }
        }
        toast.success(`Created group "${input.name}"`)
      }
      onSaved()
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to save group',
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {group ? `Edit group "${group.name}"` : 'New property group'}
          </DialogTitle>
          <DialogDescription>
            Group properties to reuse them across scenarios, handoff rules, and
            knowledge bases.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="property-group-name">Name</Label>
            <Input
              id="property-group-name"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Downtown portfolio"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="property-group-description">
              Description (optional)
            </Label>
            <Textarea
              id="property-group-description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={2}
            />
          </div>
          <div className="space-y-2">
            <Label>Properties</Label>
            <SearchDropdown<Property>
              items={items}
              selectedItems={selectedItems}
              onSelect={handleSelect}
              multiSelect
              placeholder="Select properties"
              searchPlaceholder="Search properties..."
              buttonClassName="w-full"
              emptyMessage="No properties found"
              renderItem={(item, isSelected) => (
                <>
                  {item.icon}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{item.label}</div>
                    {item.sublabel && (
                      <div className="text-xs text-muted-foreground truncate mt-1">
                        {item.sublabel}
                      </div>
                    )}
                  </div>
                  {isSelected && <Check className="h-4 w-4 shrink-0" />}
                </>
              )}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? 'Saving…' : group ? 'Save changes' : 'Create group'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function DeleteGroupDialog(props: {
  group: PropertyGroupWithMembers
  companyId: string
  isSuperAdmin: boolean
  onDeleted: () => void
  onClose: () => void
}) {
  const { group, companyId, isSuperAdmin, onDeleted, onClose } = props
  const [impact, setImpact] = useState<PropertyGroupDeleteImpact | null>(null)
  const [confirming, setConfirming] = useState(false)

  useEffect(() => {
    const fetch = isSuperAdmin
      ? directoryAdminApiClient.previewPropertyGroupDelete(companyId, group.id)
      : directoryApiClient.previewPropertyGroupDelete(group.id)
    fetch.then(setImpact).catch(() =>
      setImpact({
        members: 0,
        knowledgeBases: 0,
        scenarios: 0,
        workflows: 0,
        scenariosLosingScope: 0,
        workflowsLosingScope: 0,
      }),
    )
  }, [companyId, group.id, isSuperAdmin])

  const conditionRefs = (impact?.scenarios ?? 0) + (impact?.workflows ?? 0)
  const losingScope =
    (impact?.scenariosLosingScope ?? 0) + (impact?.workflowsLosingScope ?? 0)

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          {/* Base dialog title/description carry mb-8/mb-6 — too airy for a
              three-line confirm; tighten locally. */}
          <DialogTitle className="mb-2">
            Delete group "{group.name}"?
          </DialogTitle>
          <DialogDescription className="mb-0">
            Memberships are removed with the group.
          </DialogDescription>
        </DialogHeader>
        <div className="pb-3">
          {impact == null ? (
            <Skeleton className="h-12 w-full" />
          ) : (
            <Text
              variant="muted"
              className={
                impact.knowledgeBases + conditionRefs > 0
                  ? 'text-amber-600 dark:text-amber-500'
                  : undefined
              }
            >
              The group will be removed from {impact.scenarios} scenario(s),{' '}
              {impact.workflows} handoff rule(s), and {impact.knowledgeBases}{' '}
              knowledge base(s) that reference it.
              {losingScope > 0 &&
                ` ${losingScope} of them ${losingScope === 1 ? 'is' : 'are'} scoped only to this group and will apply to ALL properties after deletion.`}
              {conditionRefs > 0 &&
                losingScope === 0 &&
                ' Each keeps its remaining property scope.'}
            </Text>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={confirming || impact == null}
            onClick={async () => {
              setConfirming(true)
              try {
                if (isSuperAdmin) {
                  await directoryAdminApiClient.deletePropertyGroup(
                    companyId,
                    group.id,
                  )
                } else {
                  await directoryApiClient.deletePropertyGroup(group.id)
                }
                toast.success(`Deleted group "${group.name}"`)
                onDeleted()
              } catch (error) {
                toast.error(
                  error instanceof Error
                    ? error.message
                    : 'Failed to delete group',
                )
              } finally {
                setConfirming(false)
              }
            }}
          >
            {confirming ? 'Deleting…' : 'Confirm delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
